import React from "react";
import { Main } from "./style";
import axios from "axios";
import { webURL } from "../../../services/API";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import { BarChart3, CheckCircle2, GitBranch, X, XCircle } from "lucide-react";
import styled from "styled-components";
import moment from "moment";

export function MonitorLog({ projectId }: { projectId: string }) {
   const settingsIsRun = useSelector((state: RootState) => state.settings.data.isRunForProcess);
   const [isToggle, setIsToggle] = React.useState(false);
   const [items, setItems] = React.useState<{ date: string, success: number, failure: number }[]>([]);
   const [errors, setErrors] = React.useState<{ description: string, registration: string }[]>([]);

   // ---
   React.useEffect(() => {
      if ((projectId && !settingsIsRun) || settingsIsRun) {
         (async function () {
            const monitor = (await axios.get(`${webURL}/process/monitor?projectId=${projectId}`)).data;
            setItems(monitor.items);
            setErrors(monitor.error);
         })();
      }
   }, [projectId, settingsIsRun]);

   // ---
   const successTotal = items.reduce((total, item) => total + item.success, 0);

   return (
      <Main>
         <div className="boxLog" onClick={ _=> setIsToggle(true) }>
            <div className="icon"><BarChart3 size="16px" /></div>
            <div className="info">
               <span>Monitoring</span>
               <div>
                  <span><BarChart3 size="12px" color="rgb(249, 115, 22)"/> { items.reduce((total, item) => total + item.success + item.failure, 0) } Total</span>
                  <span><GitBranch size="12px" color="rgb(34, 197, 94)" /> { successTotal } Success</span>
               </div>
            </div>
         </div>
         { isToggle ? <Popup onClick={ _=> setIsToggle(false) }>
            <div className="direct" onClick={ e => e.stopPropagation() }>
               <div className="close" onClick={ _=> setIsToggle(false) }>
                  <X size={ 19 } />
               </div>
               <div className="iconTitle">
                  <div><BarChart3 size={ 20 } /></div>
                  <h3>Monitoring Report <span>For current workflow</span></h3>
               </div>
               <div className="cards">
                  <div className="card">
                     <div>
                        <span className="title">Total Success</span>
                        <span className="num">{ successTotal }</span>
                     </div>
                     <CheckCircle2 size={ 30 } />
                  </div>
                  <div className="card">
                     <div>
                        <span className="title">Total Failures</span>
                        <span className="num">{ items.reduce((total, item) => total + item.failure, 0) }</span>
                     </div>
                     <XCircle size={ 30 } />
                  </div>
               </div>
               <div className="table">
                  <ul className="titles">
                     <li>Date</li>
                     <li>Success</li>
                     <li>Failure</li>
                  </ul>
                  <div className="scroll">{
                     items.length ? items.map((item, index) => (
                        <ul key={ index.toString() } className="row">
                           <li>{ item.date }</li>
                           <li><span><CheckCircle2 size={ 14 } /> { item.success }</span></li>
                           <li><span><XCircle size={ 14 } /> { item.failure }</span></li>
                        </ul>
                     )) : <div className="empty">The workflow has not been run yet!</div>   
                  }</div>
               </div>
            </div>
            <div className="log direct" onClick={ e => e.stopPropagation() }>
               <h3 style={{ fontWeight: 400 }}>Workflow Error Log</h3>
               { errors.length ? <ul>{
                  errors.map((error, index) => (
                     <li key={ index.toString() }>
                        <div>
                           <div className="status">
                              <XCircle size={ 14 } />
                              <span>ERROR</span>
                           </div>
                           <span>{ moment(error.registration).fromNow() }</span>
                        </div>
                        <pre>{ error.description }</pre>
                     </li>
                  ))   
               }</ul> : <div className="empty">No errors have occurred yet!</div> }
            </div>
         </Popup> : null }
      </Main>
   );
}

const Popup = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background-color: rgba(30, 39, 46, 0.65);
   z-index: 99;

   .direct {
      display: flex;
      flex-direction: column;
      position: relative;
      background-color: #fff;
      padding: 30px;
      border-radius: 5px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
      width: 550px;
      height: 550px;

      &:first-of-type {
         margin-right: 15px;
      }
      .close {
         position: absolute;
         top: 20px;
         right: 20px;
         opacity: 0.65;
         font-size: 20px;
         padding: 5px 10px;
         transition: all .2s ease-in-out;
         cursor: pointer;

         &:hover {
            opacity: 0.8;
         }
      }
      .iconTitle {
         display: flex;
         align-items: center;
         margin-bottom: 30px;

         > div {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            background-image: linear-gradient(to right, #f97316, #ef4444);
            color: #fff;
            border-radius: 5px;
            margin-right: 10px;
         }
         h3 {
            display: flex;
            align-items: center;
            font-weight: 400;

            span {
               opacity: 0.8;
               font-weight: 500;
               font-size: 13px;
               margin-left: 7.5px;
               background-color: rgb(255, 237, 213);
               color: rgb(154, 52, 18);
               border: 1px solid rgb(254, 215, 170);
               border-radius: 25px;
               padding: 3px 15px;
            }
         }
      }
      .cards {
         display: flex;
         justify-content: space-between;
         margin-bottom: 30px;
         
         .card { 
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: calc(50% - 7.5px);
            box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
            padding: 15px;
            border-radius: 10px;

            &:first-of-type {
               border: 1px solid rgb(187, 247, 208);
               background-image: linear-gradient(to bottom right, #f0fdf4, #ecfdf5);
               
               span:first-of-type {
                  color: rgb(22, 163, 74);
               }
               span:last-of-type {
                  color: rgb(22, 101, 52);
               }
               svg {
                  color: rgb(54, 206, 110);
               }
            }
            &:last-of-type {
               border: 1px solid rgb(254, 202, 202);
               background-image: linear-gradient(to bottom right, #fef2f2, #fff1f2);

               span:first-of-type {
                  color: rgb(220, 38, 38);
               }
               span:last-of-type {
                  color: rgb(153, 27, 27);
               }
               svg {
                  color: rgb(255, 87, 87);
               }
            }
            > div {
               span {
                  display: block;

                  &:first-of-type {
                     font-size: 15px;
                     margin-bottom: 5px;
                  }
                  &:last-of-type {
                     font-weight: 500;
                  }
               }
            }
         }
      }
      .table {
         display: flex;
         flex-direction: column;
         height: calc(100% - 36px - 30px - 76.6px - 30px);
         box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
         border: 1px solid rgb(226, 232, 240);
         border-radius: 10px;

         ul {
            display: flex;
            opacity: 0.8;

            li {
               display: flex;
               align-items: center;
               padding: 0 20px;

               &:not(:first-of-type) {
                  width: 25%;
               }
               &:first-of-type {
                  width: 50%;
               }
            }
         }
         .titles {
            display: flex;
            align-items: center;
            background-image: linear-gradient(to right, #f8fafc, #eff6ff);
            border-bottom: 1px solid rgb(226, 232, 240);
            font-size: 16px;
            font-weight: 500;
            border-radius: 10px 10px 0 0;
            height: 60px;
         }
         .row {
            display: flex;
            align-items: center;
            // opacity: 0.5;
            height: 45px;
            font-size: 17px;
            
            &:not(:last-of-type) {
               border-bottom: 1px solid rgb(226, 232, 240);
            }
            li {
               span {
                  display: inline-flex;
                  justify-content: center;
                  align-items: center;
                  padding: 1px 10px;
                  font-size: 15px;
                  border-radius: 25px;

                  svg {
                     margin-right: 5px;
                  }
               }
               &:nth-of-type(2) span {
                  color: rgb(22, 101, 52);
                  background-color: rgb(220, 252, 231);
                  border: 1px solid rgb(187, 247, 208);
               }
               &:last-of-type span {
                  color: rgb(153, 27, 27);
                  background-color: rgb(254, 226, 226);
                  border: 1px solid rgb(254, 202, 202);
               }
            }
         }
         .scroll {
            overflow: auto;
            height: 409.4px;

            &::-webkit-scrollbar {
               width: 0;
            }
         }
      }
   }
   .log {
      ul {
         margin-top: 15px;
         height: 100%;
         overflow: auto;
         
         li {
            display: flex;
            flex-direction: column;
            border: 1px solid rgb(218, 225, 233);
            font-size: 15px;
            border-radius: 10px;
            padding: 15px;

            &:not(:last-of-type) {
               margin-bottom: 15px;
            }
            > div {
               display: flex;
               align-items: center;
               justify-content: space-between;

               .status {
                  display: flex;
                  align-items: center;

                  svg {
                     margin-right: 7.5px;
                     color: rgb(239, 68, 68);
                  }
                  span {
                     font-size: 13px;
                     color: rgb(153, 27, 27);
                     background-color: rgb(254, 242, 242);
                     border: 1px solid rgb(254, 202, 202);
                     border-radius: 25px;
                     padding: 1px 10px;
                  }
               }
               > span {
                  opacity: 0.65;
               }
            }
            pre {
               white-space: pre-wrap;
               word-wrap: break-word;
               word-break: break-word;
               margin-top: 7.5px;
               opacity: 0.9;
            }
         }
      }
   }
   .empty {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      opacity: 0.3;
   }
`;